import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Upload, Download, Trash2, X, Image, File, FileSpreadsheet, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeDocuments = [
  { value: 'plan', label: 'Plan de l\'immeuble', icon: '📐' },
  { value: 'notice_technique', label: 'Notice technique', icon: '📋' },
  { value: 'rapport_controle', label: 'Rapport de contrôle', icon: '✅' },
  { value: 'diagnostic', label: 'Diagnostic', icon: '🔍' },
  { value: 'photo_avant_apres', label: 'Photo avant/après travaux', icon: '📸' },
  { value: 'facture', label: 'Facture', icon: '🧾' },
  { value: 'contrat', label: 'Contrat', icon: '📄' },
  { value: 'pv_ag', label: 'PV d\'assemblée générale', icon: '📝' },
  { value: 'reglement', label: 'Règlement de copropriété', icon: '📕' },
  { value: 'autre', label: 'Autre document', icon: '📁' },
];

export default function DocumentsTab({ buildingId, token }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/carnet-entretien/documents/${buildingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) setDocuments(await response.json());
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, [buildingId, token]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('building_id', buildingId);
      formData.append('type_document', form.type_document || 'autre');
      formData.append('nom', form.nom || selectedFile.name);
      formData.append('description', form.description || '');
      
      const response = await fetch(`${API_URL}/api/carnet-entretien/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Document ajouté');
        setShowModal(false);
        setForm({});
        setSelectedFile(null);
        fetchDocuments();
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await fetch(`${API_URL}/api/carnet-entretien/documents/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast.success('Document supprimé');
      fetchDocuments();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(`${API_URL}/api/carnet-entretien/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.nom_fichier || doc.nom;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      toast.error('Erreur de téléchargement');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (doc) => {
    const ext = doc.nom_fichier?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image className="h-5 w-5 text-green-500" />;
    if (['pdf'].includes(ext)) return <FileText className="h-5 w-5 text-red-500" />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    return <File className="h-5 w-5 text-blue-500" />;
  };

  const filtered = filterType ? documents.filter(d => d.type_document === filterType) : documents;

  const groupedByType = typeDocuments.map(type => ({
    ...type,
    docs: documents.filter(d => d.type_document === type.value)
  })).filter(g => g.docs.length > 0);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Documents & Annexes</h2>
          <p className="text-sm text-gray-500">{documents.length} document(s) - Plans, rapports, diagnostics, photos...</p>
        </div>
        <button onClick={() => { setForm({ type_document: 'autre' }); setShowModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892]" data-testid="add-document-btn">
          <Upload className="h-5 w-5" />Ajouter un document
        </button>
      </div>

      {/* Filtre */}
      <div className="flex gap-3">
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">Tous les types</option>
          {typeDocuments.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
        </select>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Aucun document enregistré</p>
          <p className="text-sm text-gray-400 mt-1">Ajoutez des plans, rapports de contrôle, diagnostics et photos</p>
        </div>
      ) : filterType ? (
        // Vue filtrée - Liste simple
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date ajout</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taille</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(doc => {
                const typeDoc = typeDocuments.find(t => t.value === doc.type_document) || typeDocuments[typeDocuments.length-1];
                return (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc)}
                        <div>
                          <p className="font-medium text-gray-900">{doc.nom}</p>
                          {doc.description && <p className="text-xs text-gray-500 truncate max-w-xs">{doc.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-gray-600">{typeDoc.icon} {typeDoc.label}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(doc.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatSize(doc.taille)}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleDownload(doc)} className="p-1 hover:bg-gray-100 rounded" title="Télécharger"><Download className="h-4 w-4 text-gray-400" /></button>
                      <button onClick={() => handleDelete(doc.id)} className="p-1 hover:bg-red-50 rounded" title="Supprimer"><Trash2 className="h-4 w-4 text-red-400" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // Vue par catégorie
        <div className="space-y-6">
          {groupedByType.map(group => (
            <div key={group.value} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="font-medium text-gray-900">{group.icon} {group.label} ({group.docs.length})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {group.docs.map(doc => (
                  <div key={doc.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      {getFileIcon(doc)}
                      <div>
                        <p className="font-medium text-gray-900">{doc.nom}</p>
                        <p className="text-xs text-gray-500">{formatDate(doc.created_at)} • {formatSize(doc.taille)}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleDownload(doc)} className="p-2 hover:bg-gray-100 rounded" title="Télécharger"><Download className="h-4 w-4 text-gray-400" /></button>
                      <button onClick={() => handleDelete(doc.id)} className="p-2 hover:bg-red-50 rounded" title="Supprimer"><Trash2 className="h-4 w-4 text-red-400" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Upload */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">Ajouter un document</h3>
              <button onClick={() => { setShowModal(false); setSelectedFile(null); }}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleUpload} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type de document</label>
                <select value={form.type_document || 'autre'} onChange={(e) => setForm({...form, type_document: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                  {typeDocuments.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nom du document</label>
                <input type="text" value={form.nom || ''} onChange={(e) => setForm({...form, nom: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Plan du sous-sol" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={form.description || ''} onChange={(e) => setForm({...form, description: e.target.value})} rows="2" className="w-full px-3 py-2 border rounded-lg" placeholder="Description optionnelle..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Fichier *</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {selectedFile ? (
                    <div>
                      <p className="font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">{formatSize(selectedFile.size)}</p>
                      <button type="button" onClick={() => setSelectedFile(null)} className="mt-2 text-sm text-red-600 hover:underline">Retirer</button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <label className="cursor-pointer">
                        <span className="text-[#00D3A8] hover:underline">Choisir un fichier</span>
                        <input type="file" className="hidden" onChange={(e) => setSelectedFile(e.target.files[0])} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp" />
                      </label>
                      <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Images (max 50 Mo)</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setSelectedFile(null); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" disabled={uploading || !selectedFile} className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892] disabled:opacity-50">
                  {uploading ? 'Upload...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
